import React from "react";
import MenuItem from "./MenuItem";
import Logo from "./Logo";
import SocialIcon from "./SocialIcon";
export default function Footer({
  links = [
    { id: "home", label: "Home", href: "#home" },
    { id: "portfolio", label: "Portfolio", href: "#portfolio", active: true },
    { id: "about", label: "About", href: "#about" },
    { id: "contact", label: "Contact", href: "#contact" },
  ],
  social = [
    { id: "linkedin", name: "linkedin", href: "#linkedin", label: "LinkedIn" },
    { id: "youtube", name: "youtube", href: "#youtube", label: "YouTube" },
    {
      id: "instagram",
      name: "instagram",
      href: "#instagram",
      label: "Instagram",
    },
    { id: "tiktok", name: "tiktok", href: "#podcast", label: "TikTok" },
    { id: "facebook", name: "facebook", href: "#facebook", label: "facebook" },
  ],
  // Optional logo; defaults now handled by Logo component itself
  logo = {},
  // Optional special items area rendered under social row.
  // Accepts array of strings, React nodes, or objects like { src, alt, href, text }
  specialItems = [],
  copyright = "Copyright © 2025 All Rights Reserved",
  helloText = "Say Hello",
  email = "info@therelentessminds.com",
}) {
  return (
    <footer className="rm-footer">
      <div className="container">
        {/* Top nav links using MenuItem */}
        <nav className="rm-footer__nav">
          {links.map((l) => (
            <a
              key={l.id}
              href={l.href || "#"}
              className={["rm-footer__link", l.active ? "is-active" : ""].join(
                " ",
              )}
            >
              <MenuItem css="rm-footer-menu-item" label={l.label} side="left" />
            </a>
          ))}
        </nav>

        {/* Social row */}
        <div className="rm-footer__social">
          {social.map((s) => {
            const aria = s.label || s.id || s.name;
            const size = "lg";
            // Normalize icon inputs:
            // - If `s.src` is a path (starts with '/' or ends with .svg), treat as explicit src
            // - Otherwise, use `s.name` or token in `s.src` as the icon name
            const explicitSrc =
              typeof s.src === "string" &&
              (s.src.startsWith("/") || s.src.endsWith(".svg"))
                ? s.src
                : undefined;
            const iconName = s.name || (explicitSrc ? undefined : s.src);
            const isExternal =
              typeof s.href === "string" && /^(https?:)?\/\//i.test(s.href);
            return (
              <a
                key={s.id || s.label || iconName}
                className="rm-footer__icon"
                href={s.href || "#"}
                aria-label={aria}
                title={aria}
                target={isExternal ? "_blank" : undefined}
                rel={isExternal ? "noopener noreferrer" : undefined}
              >
                {iconName || explicitSrc ? (
                  <SocialIcon
                    name={iconName}
                    src={explicitSrc}
                    size={size}
                    asMask={false}
                    aria-label={aria}
                  />
                ) : null}
              </a>
            );
          })}
        </div>

        {/* Special items row (logos, awards, or text) */}
        {specialItems && specialItems.length > 0 ? (
          <div className="rm-footer__special">
            {specialItems.map((item, idx) => {
              // Allow string, React node, or object with src/alt/href/text
              if (typeof item === "string") {
                return (
                  <div className="rm-footer__special-item" key={idx}>
                    {item}
                  </div>
                );
              }
              if (React.isValidElement(item)) {
                return (
                  <div className="rm-footer__special-item" key={idx}>
                    {item}
                  </div>
                );
              }
              // Object case
              const { src, alt, href, text } = item || {};
              const content = (
                <>
                  {src ? (
                    <img
                      src={src}
                      alt={alt || ""}
                      className="rm-footer__special-img"
                    />
                  ) : null}
                  {text ? (
                    <span className="rm-footer__special-text">{text}</span>
                  ) : null}
                </>
              );
              return (
                <div className="rm-footer__special-item" key={idx}>
                  {href ? (
                    <a
                      href={href}
                      target={item.target || undefined}
                      rel={item.rel || undefined}
                    >
                      {content}
                    </a>
                  ) : (
                    content
                  )}
                </div>
              );
            })}
          </div>
        ) : null}

        {/* Bottom row */}
        <div className="rm-footer__bottom">
          <div className="rm-footer__col col">
            {logo ? (
              <Logo
                partClass="rm-footer__logo"
                href={logo.href}
                alt={logo.alt}
                icon={logo.icon}
                wordmark={logo.wordmark}
                items={logo.items}
              />
            ) : null}
            {logo ? (
              <span className="rm-footer__divider" aria-hidden>
                |
              </span>
            ) : null}
            <div className="rm-footer__copyright">{copyright}</div>
          </div>
          <div className="rm-footer__col rm-footer__col--right col">
            <span className="rm-footer__hello">{helloText}</span>
            <a className="rm-footer__email" href={`mailto:${email}`}>
              {email}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
